// Blåtur invitation list as members see it: who is invited, strikes, and applying to be readmitted

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { normalizeName } from './adminData';
import { APPLY_TO } from './blaturRules';
import BlaturAspiranter from './BlaturAspiranter';
import { tripYear } from './blaturData';
import { SmartImage } from './imageUtils';
import {
  STRIKES_TO_OUT, memberStatus, loadMembers, loadMyApplications, applyForReadmission,
} from './blaturMembers';

const initials = (name) => name.trim().split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();

// Everyone in the blåtur gang with their profile picture. Those who are out are shown greyed out as not active.
function BlaturGjengen({ people, profileImages, userMap }) {
  return (
    <section className="blatur-section">
      <h2>Blåturgjengen</h2>
      <div className="blatur-gang">
        {people.map(p => {
          const key = p.name.trim().toLowerCase();
          const userId = userMap[p.name.toLowerCase()] || userMap[key];
          const content = (
            <>
              <div className="blatur-avatar">
                {profileImages[key] ? <SmartImage src={profileImages[key]} size={200} /> : <span>{initials(p.name)}</span>}
              </div>
              <div className="blatur-gang-name">{p.name.trim()}</div>
              <div className="blatur-gang-meta">{p.out ? 'Ikke aktiv' : `${p.trips} ${p.trips === 1 ? 'tur' : 'turer'}`}</div>
            </>
          );
          return userId
            ? <Link key={p.name} to={`/profil/${userId}`} className={`blatur-gang-person${p.out ? ' out' : ''}`} title={p.out ? `${p.name.trim()} er ikke aktiv` : p.name.trim()}>{content}</Link>
            : <div key={p.name} className={`blatur-gang-person${p.out ? ' out' : ''}`} title={p.out ? `${p.name.trim()} er ikke aktiv` : p.name.trim()}>{content}</div>;
        })}
      </div>
      {people.some(p => p.out) && <p className="blatur-legend">De grå er ikke aktive etter å ha uteblitt fra {STRIKES_TO_OUT} turer på rad. De kan søke om å komme tilbake.</p>}
    </section>
  );
}

function BlaturMedlemmer({ user, myName, trips, profileImages = {}, userMap = {} }) {
  const [members, setMembers] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const [m, a] = await Promise.all([loadMembers(), loadMyApplications(user.uid)]);
    setMembers(m);
    setMyApplications(a);
  }, [user.uid]);

  useEffect(() => {
    load().catch(err => {
      console.error('Error fetching members: ', err);
      setError(err);
    });
  }, [load]);

  const { lastTrip, tripCount, everyone } = useMemo(() => {
    const last = {};
    const count = {};
    const names = new Map();
    trips.forEach(trip => new Set([...(trip.Deltagelse || []), ...(trip.Vert || [])].map(n => n.trim())).forEach(n => {
      if (!n) return;
      const key = normalizeName(n);
      last[key] = Math.max(last[key] || 0, tripYear(trip));
      count[key] = (count[key] || 0) + 1;
      names.set(key, n);
    }));
    return { lastTrip: last, tripCount: count, everyone: [...names.values()] };
  }, [trips]);

  const tripYears = [...new Set(trips.map(tripYear))];

  if (error) {
    return (
      <div className="blatur-empty">
        Kunne ikke hente invitasjonslisten.
        {error.code === 'permission-denied' && ' Sikkerhetsreglene i Firebase er trolig ikke oppdatert ennå.'}
      </div>
    );
  }
  if (!members) return <div className="blatur-empty">Laster…</div>;

  // Before the invitation list is set up: show everyone who has been on a blåtur
  if (members.length === 0) {
    const people = everyone.map(name => ({ name, out: false, trips: tripCount[normalizeName(name)] }))
      .sort((a, b) => b.trips - a.trips || a.name.localeCompare(b.name, 'no'));
    return (
      <div className="blatur-regler">
        <BlaturGjengen people={people} profileImages={profileImages} userMap={userMap} />
        <div className="blatur-empty">Invitasjonslisten er ikke satt opp ennå.</div>
        <BlaturAspiranter user={user} myName={myName} memberRows={[]} profileImages={profileImages} onAdmitted={load} />
      </div>
    );
  }

  const rows = members.map(m => ({ ...m, ...memberStatus(m, tripYears) }));
  const me = rows.find(r => normalizeName(r.name) === normalizeName(myName));
  const pendingApplication = myApplications.find(a => a.status === 'pending');

  const apply = async () => {
    setSending(true);
    try {
      await applyForReadmission({ name: myName, uid: user.uid, text });
      setText('');
      await load();
    } catch (error) {
      alert('Kunne ikke sende søknaden: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const gang = rows.map(r => ({ name: r.name, out: r.out, trips: tripCount[normalizeName(r.name)] || 0 }))
    .sort((a, b) => a.out - b.out || b.trips - a.trips || a.name.localeCompare(b.name, 'no'));

  return (
    <div className="blatur-regler">
      <BlaturGjengen people={gang} profileImages={profileImages} userMap={userMap} />

      {me && (
        <section className="blatur-section">
          <h2>Din status</h2>
          {me.out ? (
            <>
              <p>
                Du har uteblitt fra {STRIKES_TO_OUT} turer på rad og står ikke på invitasjonslisten.
                Du kan søke om å komme inn igjen, enten her eller skriftlig til {APPLY_TO}. Regn med en straff.
              </p>
              {pendingApplication ? (
                <p className="blatur-note">Søknaden din er sendt og venter på behandling.</p>
              ) : (
                <div className="blatur-apply">
                  <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Hvorfor bør du få komme tilbake?" />
                  <button className="button-17" onClick={apply} disabled={sending || !text.trim()}>Send søknad</button>
                </div>
              )}
            </>
          ) : (
            <p>Du står på invitasjonslisten{me.inARow > 0 ? '. Du uteble fra forrige tur, så uteblir du fra neste også, er du ute' : ''}.</p>
          )}
        </section>
      )}

      <section className="blatur-section">
        <h2>Invitasjonsliste</h2>
        <p className="blatur-muted">
          Uteblir du fra {STRIKES_TO_OUT} turer på rad uten god grunn, er du ute. Regelen praktiseres fra høsten 2026.
        </p>
        <div className="blatur-matrix-wrap">
          <table className="blatur-matrix">
            <thead>
              <tr><th className="blatur-matrix-name">Navn</th><th>Status</th><th>Strikes</th><th>Sist med</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="blatur-matrix-name">{r.name}</td>
                  <td>{r.out ? 'Ikke aktiv' : 'Invitert'}</td>
                  <td>{r.strikes > 0 ? '❌'.repeat(r.strikes) : ''}</td>
                  <td>{lastTrip[normalizeName(r.name)] || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <BlaturAspiranter user={user} myName={myName} memberRows={rows} profileImages={profileImages} onAdmitted={load} />
    </div>
  );
}

export default BlaturMedlemmer;
